"use client";

import Link from "next/link";
import { useEffect, useRef } from "react";
import { usePathname } from "next/navigation";
import type { NavItem } from "@/lib/nav";

type MobileNavProps = {
  items: NavItem[];
  colors: string[];
};

/**
 * The under-lg disclosure menu. A <details> element does not close by itself
 * on a client-side navigation, so it is closed explicitly when the route
 * changes — otherwise the menu sits over the page it just opened.
 */
export default function MobileNav({ items, colors }: MobileNavProps) {
  const detailsRef = useRef<HTMLDetailsElement>(null);
  const pathname = usePathname();

  useEffect(() => {
    if (detailsRef.current) detailsRef.current.open = false;
  }, [pathname]);

  return (
    <details ref={detailsRef} className="relative lg:hidden">
      <summary className="cursor-pointer list-none rounded-full bg-[#08a997] px-5 py-3 text-lg font-extrabold text-white">
        Menu
      </summary>
      {/* Subcategories are listed inline rather than in a nested disclosure —
          a disclosure inside a disclosure is two taps to reach a page. */}
      <nav className="absolute right-0 top-14 z-50 flex w-72 flex-col gap-3 rounded-2xl border-2 border-black bg-white p-4 shadow-xl">
        {items.map((link, index) => (
          <div key={link.href}>
            <Link
              href={link.href}
              onClick={() => detailsRef.current?.removeAttribute("open")}
              className="block rounded-full px-5 py-3 text-center text-lg font-extrabold text-white"
              style={{ backgroundColor: colors[index % colors.length] }}
            >
              {link.label}
            </Link>
            {link.children && (
              <ul className="mt-2 space-y-1 pl-4">
                {link.children.map((child) => (
                  <li key={child.href}>
                    <Link
                      href={child.href}
                      onClick={() => detailsRef.current?.removeAttribute("open")}
                      className="block rounded-xl px-4 py-2 text-base font-bold text-[#35263a]"
                    >
                      {child.label}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </nav>
    </details>
  );
}
